import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type { OrganizationOut } from '@/api/generated/schemas';
import { OrganizationStatusTag } from '@/components/tenancy/tags';
import { ORGANIZATION_SHOW_TABS } from '../constants';
import type { OrganizationShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<OrganizationShowTab, string> = {
  general: 'base.sections.basic_info',
  members: 'tenancy.organizations.sections.members',
  publishers: 'tenancy.organizations.sections.publishers',
  contracts: 'tenancy.organizations.sections.contracts',
};

interface OrganizationShowHeaderProps {
  record: OrganizationOut;
  activeTab: OrganizationShowTab;
  onTabChange: (key: string) => void;
}

export const OrganizationShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: OrganizationShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Title>
            <OrganizationStatusTag status={record.status} />
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('tenancy.organizations.fields.members_count')}>
              {record.members.length}
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={ORGANIZATION_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
